import type { ApiPhoneCall } from '../../../api/types';
import type { ActionReturnType } from '../../types';

import { isLiveKitGroupActive } from '../../../lib/vibecalls/group/livekitGroupRoom';
import { isLiveKitRoomActive } from '../../../lib/vibecalls/phone/livekitRoom';
import {
  connectPhoneCallRoom, hasAnsweredCall, isPhoneCallLiveKitEnabled, markCallAnswered,
} from '../../../lib/vibecalls/phone/phoneCallLiveKit';
import { getChatTitle } from '../../../global/helpers/chats';
import { ARE_CALLS_SUPPORTED } from '../../../util/browser/windowEnvironment';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { omit } from '../../../util/iteratees';
import { notifyAboutCall } from '../../../util/notifications';
import * as langProvider from '../../../util/oldLangProvider';
import { onTickEnd } from '../../../util/schedulers';
import { callApi } from '../../../api/client';
import { addActionHandler, getGlobal, setGlobal } from '../../index';
import { updateChat, updateChatFullInfo } from '../../reducers';
import { removeGroupCall, updateGroupCall, updateGroupCallParticipant } from '../../reducers/calls';
import { updateTabState } from '../../reducers/tabs';
import { selectChat } from '../../selectors';
import { selectGroupCall, selectPhoneCallUser } from '../../selectors/calls';
import {
  checkNavigatorUserMediaPermissions, initializeSounds, stopGroupCallSound, takePendingCallCancel,
} from '../ui/calls';

addActionHandler('apiUpdate', (global, actions, update): ActionReturnType => {
  switch (update['@type']) {
    case 'updateGroupCall': {
      if (update.call.connectionState === 'discarded') {
        if (global.groupCalls.activeGroupCallId) {
          if ('leaveGroupCall' in actions) actions.leaveGroupCall({ shouldRemove: true, tabId: getCurrentTabId() });
          return undefined;
        } else {
          // The call may have never been joined (we only heard the `groupCallStarted` notification
          // for it) — stop it now in case it is still playing.
          stopGroupCallSound('groupCallStarted');
          return removeGroupCall(global, update.call.id);
        }
      }

      const groupCall = selectGroupCall(global, update.call.id);
      const chatId = groupCall?.chatId;
      if (chatId) {
        global = updateChat(global, chatId, {
          isCallNotEmpty: (groupCall.participantsCount > 0 || Boolean(groupCall.participants?.length)),
        });
      }

      // When LiveKit owns the call we're in, its room events are the single source of truth for the
      // participant map. The backend's `updateGroupCall` carries a full `participants` map with a
      // stale `isMuted: true` for everyone (LiveKit mic toggles are never sent to the backend), so
      // merging it resets our own mic on every join/leave. Drop `participants` for the active call.
      const isLiveKitOwned = isLiveKitGroupActive() && global.groupCalls.activeGroupCallId === update.call.id;

      return updateGroupCall(
        global,
        update.call.id,
        omit(update.call, isLiveKitOwned ? ['connectionState', 'participants'] : ['connectionState']),
        undefined,
        update.call.participantsCount,
      );
    }
    case 'updateGroupCallChatId': {
      const chat = selectChat(global, update.chatId);
      if (chat) {
        global = updateChatFullInfo(global, update.chatId, {
          groupCallId: update.call.id,
        });
        // `isCallActive` gates the header "Join Voice Chat" option (`canEnterVoiceChat`) + the join
        // bar's fallback path; set it from whether a call is linked (cleared on discard).
        global = updateChat(global, update.chatId, {
          isCallActive: Boolean(update.call.id),
        });

        // A voice chat just started in this group (id set, not a discard-clear) and we're not
        // already in a call — notify so members not looking at the chat learn about it.
        if (update.call.id && !global.groupCalls.activeGroupCallId) {
          initializeSounds();
          actions.playGroupCallSound({ sound: 'groupCallStarted' });
          const chatTitle = getChatTitle(langProvider.oldTranslate, chat);
          actions.showNotification({
            message: langProvider.oldTranslate('VoiceChatStartedNotification', chatTitle),
            tabId: getCurrentTabId(),
          });
        } else if (!update.call.id) {
          // The voice chat was unlinked from this group without us ever joining it — stop the
          // notification in case it is still playing.
          stopGroupCallSound('groupCallStarted');
        }
      }
      return global;
    }
    case 'updateGroupCallParticipants': {
      const { groupCallId, participants, nextOffset } = update;
      const { currentUserId } = global;

      // When LiveKit owns the call we're in, its room events are the single source of truth for the
      // participant map (membership, mic and stream flags). Merging the backend's WS broadcast on top
      // clobbers that live state — e.g. resetting our own mic back to the backend's stale value on any
      // join/leave, so the mic button won't stay off. Skip the merge for the active LiveKit call.
      if (isLiveKitGroupActive() && global.groupCalls.activeGroupCallId === groupCallId) {
        break;
      }

      // `vibecalls` should disconnect if the participant is us but from another device
      global = getGlobal();
      participants.forEach((participant) => {
        if (participant.id) {
          global = updateGroupCallParticipant(
            global, groupCallId, participant.id, participant, Boolean(nextOffset) || currentUserId === participant.id,
          );
        }
      });
      if (nextOffset) {
        global = updateGroupCall(global, groupCallId, {
          nextOffset,
        });
      }

      const groupCall = selectGroupCall(global, groupCallId);
      const chatId = groupCall?.chatId;
      if (chatId) {
        global = updateChat(global, chatId, {
          isCallNotEmpty: (groupCall.participantsCount > 0 || Boolean(groupCall.participants?.length)),
        });
      }
      return global;
    }
    case 'updatePhoneCall': {
      if (!ARE_CALLS_SUPPORTED) return undefined;

      const {
        phoneCall,
        currentUserId,
      } = global;

      // The backend's 1:1 call signaling sends `adminId`/`participantId` as raw numbers (not
      // signed peer-id strings like everywhere else — see backend `calls/shared/broadcast.ts`).
      // `ApiPhoneCall` types them as `string`, so every `=== currentUserId` comparison below (and
      // in `selectPhoneCallUser`/`PhoneCall.tsx`) needs a real string on both sides — otherwise a
      // same-person id compares unequal by type alone, which can misidentify the call as someone
      // else's and auto-discard it as "busy" (see the `isInOtherPhoneCall` check just below).
      const call: ApiPhoneCall = {
        ...update.call,
        adminId: update.call.adminId !== undefined ? String(update.call.adminId) : update.call.adminId,
        participantId: update.call.participantId !== undefined
          ? String(update.call.participantId) : update.call.participantId,
      };

      // Another call (P2P or group) is already active: auto-discard the new incoming call as busy.
      // Gate on `phoneCall.id` (not just `phoneCall`): the caller's optimistic outgoing call has an
      // empty id until the backend echoes the real one over WS, and that echo must not be mistaken
      // for a different (busy) call — otherwise our own outgoing call discards itself instantly.
      const isInOtherPhoneCall = Boolean(phoneCall?.id) && call.id !== phoneCall.id;
      const isInGroupCall = Boolean(global.groupCalls.activeGroupCallId) && !phoneCall;
      if (isInOtherPhoneCall || isInGroupCall) {
        if (call.state !== 'discarded') {
          callApi('discardCall', {
            call,
            isBusy: true,
          });
        }
        return undefined;
      }

      if (phoneCall) {
        if (call.state === 'discarded') {
          markCallAnswered(undefined);
          actions.playGroupCallSound({ sound: 'end' });

          // Commit the `discarded` state into `global.phoneCall` BEFORE calling `hangUp`, so it
          // takes its immediate-teardown branch (`phoneCall.state === 'discarded'`). Otherwise
          // `hangUp` still sees the old active state and re-sends a redundant `discardCall` to the
          // backend, which echoes another discard update that can resurrect the call and leave the
          // PhoneCall UI stuck open.
          global = {
            ...global,
            phoneCall: { ...phoneCall, ...call },
          };
          setGlobal(global);

          if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });

          global = getGlobal();
          return {
            ...global,
            ...(call.needRating && { ratingPhoneCall: call }),
          };
        }

        // Reconcile the caller's optimistic outgoing call with the backend's real `id` +
        // `roomName` echoed over WS (the optimistic call starts with an empty id), so later
        // updates match by id and `discardCall`/`hangUp` can address the real call.
        const reconciled: ApiPhoneCall = {
          ...phoneCall,
          id: phoneCall.id || call.id,
          roomName: call.roomName ?? phoneCall.roomName,
        };

        // The backend broadcasts `accepted` to every session of the callee, so this update also
        // lands on the devices that did NOT pick up. Answered elsewhere: stop ringing and close the
        // call screen. Never discard — that would end the call the other device is now on.
        const isCallerHere = call.adminId === currentUserId;
        if (call.state === 'accepted' && !isCallerHere && !hasAnsweredCall(reconciled.id)) {
          markCallAnswered(undefined);
          return {
            ...global,
            phoneCall: undefined,
          };
        }

        // The call just became `accepted` (both parties): connect the LiveKit media room
        // (peer-to-peer path). The connect helper flips the state to `active` once the room is
        // up. Skip if already active (the `active` update is what the connect helper emits).
        if (call.state === 'accepted' && isPhoneCallLiveKitEnabled() && !isLiveKitRoomActive()) {
          void connectPhoneCallRoom(reconciled);
        }

        return {
          ...global,
          phoneCall: reconciled,
        };
      }

      const isOutgoing = call?.adminId === currentUserId;

      // Our own outgoing call, which we no longer track locally: the user hung up while it was
      // still `requesting`, before it had an id to cancel with. The id is here now — cancel it, or
      // the callee rings on and can answer a call the caller already gave up on.
      if (isOutgoing && call.id && call.state !== 'discarded' && takePendingCallCancel(call.participantId)) {
        callApi('discardCall', { call });
        return undefined;
      }

      if (!isOutgoing && call.state === 'requested') {
        onTickEnd(() => {
          global = getGlobal();
          const user = selectPhoneCallUser(global);
          if (!user) return;
          notifyAboutCall({
            call,
            user,
          });
        });

        initializeSounds();
        void checkNavigatorUserMediaPermissions(global, actions, call.isVideo, getCurrentTabId());
        global = {
          ...global,
          phoneCall: call,
        };

        return updateTabState(global, {
          isCallPanelVisible: false,
        }, getCurrentTabId());
      }
    }
  }

  return undefined;
});
